import IResponseModel from "@/api/ResponseModel";
import SchoolApi from "@/api/SchoolApi";
import SchoolBankAccount from "@/models/APIModels/SchoolBankAccount";
import SchoolDetail from "@/models/APIModels/SchoolDetail";
import SchoolDetailModel from "@/models/ViewModels/SchoolDetailModel";
import { AddressTypeEnum, SchoolStatusEnum } from "@/shared/ModelEnums";

export default class SchoolUseCase {
  private readonly _schoolApi: SchoolApi;

  constructor(schoolApi: SchoolApi = new SchoolApi()) {
    this._schoolApi = schoolApi;
  }

  async getSchoolList() {
    try {
      const result = await this._schoolApi.getSchoolList();
      let schoolList: IResponseModel<{ label: string; value: string }[]>;
      schoolList = { ...result } as IResponseModel<
        { label: string; value: string }[]
      >;
      if (result.content)
        schoolList.content = result.content.map((item) => {
          return { label: item.name, value: item.reference };
        });
      return schoolList;
    } catch (error) {
      throw error;
    }
  }

  async getAll(filterOptions: {
    schoolRegistrationStatus?: string[];
    schoolName?: string;
    australinaStates?: string[];
    suburbOrCity?: string;
    acaraId?: number;
  }) {
    /**
     * The following is when the donor is searching for a school by acaraId.
     * This is a special case where we need to return only one school.
     */
    if (filterOptions.acaraId) {
      const result = await this._schoolApi.getSchoolByAcaraId(
        filterOptions.acaraId
      );
      const returnResult: IResponseModel<SchoolDetailModel[]> = {
        ...result,
      } as IResponseModel<SchoolDetailModel[]>;
      if (result.content)
        returnResult.content = [this.mapSchoolToViewModel(result.content)];
      return returnResult;
    }
    let queryStringArray: string[] = [];
    if (filterOptions.schoolRegistrationStatus)
      queryStringArray.push(
        `schoolRegistrationStatusCds=${filterOptions.schoolRegistrationStatus.join(
          ","
        )}`
      );
    if (filterOptions.australinaStates)
      queryStringArray.push(
        `australianStateCds=${filterOptions.australinaStates.join(",")}`
      );
    if (filterOptions.schoolName)
      queryStringArray.push(`schoolname=${filterOptions.schoolName}`);
    if (filterOptions.suburbOrCity)
      queryStringArray.push(`suburborcity=${filterOptions.suburbOrCity}`);
    // if (filterOptions.acaraId)
    //   queryStringArray.push(`acaraId=${filterOptions.acaraId}`);

    const result = await this._schoolApi.getSchoolList(
      queryStringArray.join("&")
    );

    return result;
  }

  async getSchoolByReference(reference: string) {
    try {
      const result = await this._schoolApi.getSchoolByReference(reference);

      if (result.content)
        result.content = this.mapSchoolToViewModel(result.content);

      return result;
    } catch (error) {
      throw error;
    }
  }

  async getSchoolBankAccount(reference: string) {
    try {
      const result = await this._schoolApi.getSchoolBankAccount(reference);

      return result;
    } catch (error) {
      throw error;
    }
  }

  async updateSchoolBankAccount(bankAccount: SchoolBankAccount) {
    try {
      const result = await this._schoolApi.putSchoolBankAccount(
        bankAccount.reference,
        bankAccount
      );
      return result;
    } catch (error) {
      throw error;
    }
  }

  async update(school: SchoolDetailModel) {
    school.approxStudentsNum = Number(school.approxStudentsNum);
    const result = await this._schoolApi.update(
      school.reference,
      this.mapSchoolFromViewModel(school)
    );

    if (result.content)
      result.content = this.mapSchoolToViewModel(result.content);

    return result;
  }

  async updateByAdministrator(
    school: SchoolDetailModel,
    newSchoolStatus: SchoolStatusEnum,
    statusChangeReason: string
  ) {
    const input = this.mapSchoolFromViewModel(school);
    input.approxStudentsNum = Number(school.approxStudentsNum);
    input.schoolStatusCd = newSchoolStatus;
    input.statusChangeReason = statusChangeReason;
    const result = await this._schoolApi.updateByAdministrator(
      input.reference,
      input
    );

    if (result.content)
      result.content = this.mapSchoolToViewModel(result.content);

    return result;
  }

  async insert(school: SchoolDetailModel) {
    const result = await this._schoolApi.insert(
      this.mapSchoolFromViewModel(school)
    );

    if (result.content)
      result.content = this.mapSchoolToViewModel(result.content);

    return result;
  }

  private mapSchoolFromViewModel(school: SchoolDetailModel): SchoolDetail {
    const postalAddress = school.schoolAddresses.find(
      (item) => item.addressTypeCd === AddressTypeEnum.AT_Po
    );
    return { ...school, postalAddress };
  }

  private mapSchoolToViewModel(school: SchoolDetail): SchoolDetailModel {
    const postalAddress = school.schoolAddresses.find(
      (item) => item.addressTypeCd === AddressTypeEnum.AT_Po
    );
    return { ...school, postalAddress };
  }
}
